import React from "react";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { get, size, filter, sortBy } from "lodash";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";

import TableCellStyled from "../../Common/TableCellStyled";
import TemperatureMetrics from "./TemperatureMetrics";
import TemperatureGraph from "./TemperatureGraph";

import { getAttributeValue, getAttributeName } from "../../../utils/profile";
import { getLov } from "../../../utils/lov";
import { formatDate } from "../../../utils/date";
import {
  getThresholdEvent,
  getDisplayedTemperatureAttr,
  isOverThreshold,
  getMeasureType,
  getMeasureUnit,
} from "../../../utils/temperature";

import { ISO_TYPE, MANUAL_TYPE } from "../../../constants/common";

import colors from "../../../assets/style/colors";
import images from "../../../assets/img/images";

const styles = {
  title: {
    fontWeight: 500,
    marginBottom: "10px",
    "& span": {
      marginRight: "10px",
    },
  },
  headerCell: {
    backgroundColor: colors.cardTitleBg,
    color: colors.black87,
    fontWeight: 500,
    height: "50px",
    borderRight: `3px solid ${colors.white}`,
  },
  headerCellMeasuredTemp: {
    minWidth: "120px",
    maxWidth: "120px",
    overflowWrap: "anywhere",
    width: "120px",
    lineHeight: "1.2",
  },
  colorCircle: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
  },
};

const MeasuredTemperature = (props) => {
  const { classes, data, startTime, language } = props;
  const [lov, setLov] = React.useState({});

  React.useEffect(() => {
    const fetchLov = async () => {
      setLov({
        88: await getLov(88),
      });
    };
    fetchLov();
  }, []);

  const events82 = sortBy(
    filter(
      get(data, ["events"]),
      (e) => get(e, ["type", "id"]) === 82 && get(e, ["active"])
    ),
    (e) => new Date(getAttributeValue(e, 13))
  );

  const renderTempRow = (e, index) => {
    const type = getMeasureType(e);

    return (
      <TableRow
        style={{
          backgroundColor: index % 2 === 0 ? colors.white : colors.tableDarkBg,
        }}
      >
        <TableCellStyled className="text-54">
          {formatDate(
            new Date(
              type === MANUAL_TYPE ? e.created_on : getAttributeValue(e, 13)
            ) - new Date(startTime),
            "mm:ss"
          )}
        </TableCellStyled>
        <TableCellStyled>
          <Grid container alignItems="center">
            <Grid
              item
              className={classes.colorCircle}
              style={{
                backgroundColor:
                  type === ISO_TYPE
                    ? colors.isoYellow
                    : type === MANUAL_TYPE
                    ? colors.manualTempBlue
                    : colors.bodyPink,
              }}
            />
            <Grid item className="text-54" style={{ marginLeft: "10px" }}>
              {getAttributeName(e, getDisplayedTemperatureAttr(type), language)}
            </Grid>
          </Grid>
        </TableCellStyled>
        <TableCellStyled>
          <Box
            className="flexCenter"
            style={{
              justifyContent: "center",
              width: "55px",
              height: "24px",
              borderRadius: "2px",
              color: isOverThreshold(
                getAttributeValue(e, getDisplayedTemperatureAttr(type)),
                getThresholdEvent(e)
              )
                ? colors.threatRed
                : colors.cardGreen,
              backgroundColor: isOverThreshold(
                getAttributeValue(e, getDisplayedTemperatureAttr(type)),
                getThresholdEvent(e)
              )
                ? colors.threatRed10
                : colors.cardGreen10,
            }}
          >
            {`${parseFloat(
              getAttributeValue(e, getDisplayedTemperatureAttr(type))
            ).toFixed(1)}${getMeasureUnit(e, lov)}`}
          </Box>
        </TableCellStyled>
        <TableCellStyled className="text-54">
          {formatDate(
            type === MANUAL_TYPE ? e.created_on : getAttributeValue(e, 13),
            "hh:mm:ss A"
          )}
        </TableCellStyled>
      </TableRow>
    );
  };

  const renderTempTable = () => {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCellStyled
              className={classes.headerCell}
              style={{ minWidth: "63px", maxWidth: "63px" }}
            >
              {I18n.get("site_chrono")}
            </TableCellStyled>
            <TableCellStyled
              className={classes.headerCell}
              style={{ minWidth: "347px", maxWidth: "347px" }}
            >
              {I18n.get("site_type2")}
            </TableCellStyled>
            <TableCellStyled
              className={`${classes.headerCell} ${classes.headerCellMeasuredTemp}`}
            >
              {I18n.get("site_measure")}
            </TableCellStyled>
            <TableCellStyled
              className={classes.headerCell}
              style={{ minWidth: "100px", maxWidth: "100px" }}
            >
              {I18n.get("site_timestamp")}
            </TableCellStyled>
          </TableRow>
        </TableHead>
        <TableBody>
          {events82.map((e, index) => (
            <React.Fragment key={e.id}>
              {renderTempRow(e, index)}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    );
  };

  if (
    !get(data, ["events"]) ||
    size(
      filter(get(data, ["events"]), (e) => get(e, ["type", "id"]) === 82)
    ) === 0
  ) {
    return null;
  }

  return (
    <Grid container direction="column">
      {!props.dialog && (
        <Grid item className={classes.title}>
          <span>
            <img src={images.cameraIcon} alt="camera" />
          </span>
          <span>{I18n.get("site_cameraMesuredTemp").toUpperCase()}</span>
        </Grid>
      )}
      <TemperatureGraph data={data} startTime={startTime} lov={lov} />
      <Grid
        item
        style={{ textAlign: "right", marginTop: "30px", marginBottom: "7px" }}
      >
        {!props.dialog && (
          <TemperatureMetrics
            data={data}
            startTime={startTime}
            language="en"
            lov={lov}
          />
        )}
      </Grid>
      <Grid item style={{ width: props.dialog ? "630px" : "" }}>
        {renderTempTable()}
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(MeasuredTemperature);
